// A failed deployment's `error`, made readable. One component for every place
// the dashboard shows it — the history row (compact), the history details
// dialog and DeploymentProgress's dialog — so they cannot drift apart.
//
// describeError (lib/errorFormat) does the parsing; this only lays it out: a
// red title with an icon for the kind of failure, the one-line summary in body
// text, and the full chain / stack / phase contexts behind a toggle.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Block as BlockIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Gavel as GavelIcon,
  HourglassEmpty as HourglassIcon,
  OpenInNew as OpenInNewIcon,
  Subject as LogsIcon,
} from '@mui/icons-material';
import { DeploymentRecord } from '../../types/DeploymentRecord';
import { getColors, rounded } from '../theme/tokens';
import { alpha, statusHues } from '../theme/statusTokens';
import { describeError, ErrorKind, errorToPlainText } from '../lib/errorFormat';

export interface DeploymentErrorProps {
  /** The record's raw `error` payload. Nothing renders when there is no error. */
  error: unknown;
  /** The record the error belongs to — decides whether step logs can be offered. */
  record?: DeploymentRecord;
  /** Row mode: title + a 2-line summary and the copy button, no details/actions. */
  compact?: boolean;
  /**
   * Show the record's logs. The host owns the logs view (a tab in its own
   * dialog), so the button switches to it rather than opening a second dialog.
   * Without it no logs button is offered.
   */
  onViewLogs?: () => void;
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

export const TRUNCATED_NOTE =
  'Cause was truncated at 2 KB — see the step logs for the full error.';

const KIND_ICONS: Partial<Record<ErrorKind, typeof ErrorOutlineIcon>> = {
  policy: GavelIcon,
  rejected: BlockIcon,
  'review-timeout': HourglassIcon,
};

const actionSx = {
  minWidth: 0,
  py: 0.1,
  px: 0.75,
  fontSize: '0.72rem',
  fontWeight: 600,
  textTransform: 'none',
  whiteSpace: 'nowrap',
} as const;

const DeploymentError: React.FC<DeploymentErrorProps> = ({
  error,
  record,
  compact,
  onViewLogs,
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const [copied, setCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const view = describeError(error);
  if (!view) return null;

  const Icon = KIND_ICONS[view.kind] ?? ErrorOutlineIcon;
  const title = view.title ?? 'Error';
  // Only Argo deployments publish logs, and they're fetched by deployment id —
  // the same rule as DeploymentRow's "View logs" button.
  const canViewLogs =
    !compact && !!onViewLogs && record?.metadata?.engine === 'argo' && !!record.deployment_id;
  const hasDetails = !compact && !!view.details;
  const hasActions = hasDetails || canViewLogs || (!compact && !!view.link);

  const handleCopy = async (e: React.MouseEvent) => {
    // Rows open a details dialog on click — copying mustn't do that too.
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(errorToPlainText(view));
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const copyButton = (
    <Tooltip title={copied ? 'Copied' : 'Copy error'} arrow>
      <IconButton
        size="small"
        aria-label="Copy error"
        onClick={handleCopy}
        sx={{ color: copied ? statusHues.success : c.inkMuted, flexShrink: 0 }}
      >
        {copied ? (
          <CheckIcon sx={{ fontSize: compact ? 14 : 15 }} />
        ) : (
          <CopyIcon sx={{ fontSize: compact ? 14 : 15 }} />
        )}
      </IconButton>
    </Tooltip>
  );

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
        <Icon sx={{ fontSize: 14, color: statusHues.failed, mt: '2px', flexShrink: 0 }} />
        <Typography
          variant="caption"
          component="div"
          title={view.summary}
          sx={{
            flex: 1,
            minWidth: 0,
            color: c.inkSecondary,
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          <Box component="span" sx={{ color: statusHues.failed, fontWeight: 600, mr: 0.75 }}>
            {title}
          </Box>
          <Box component="span">{view.summary}</Box>
        </Typography>
        <Box sx={{ mt: '-4px' }}>{copyButton}</Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
        <Icon sx={{ fontSize: 16, color: statusHues.failed, flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{ color: statusHues.failed, fontWeight: 600, flex: 1, minWidth: 0 }}
        >
          {title}
        </Typography>
        {copyButton}
      </Box>

      <Typography variant="body2" sx={{ color: c.ink, wordBreak: 'break-word' }}>
        {view.summary}
      </Typography>

      {view.truncated && (
        <Typography variant="caption" sx={{ color: c.inkMuted, display: 'block', mt: 0.5 }}>
          {TRUNCATED_NOTE}
        </Typography>
      )}

      {hasActions && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
          {hasDetails && (
            <Button
              size="small"
              aria-expanded={showDetails}
              onClick={e => {
                e.stopPropagation();
                setShowDetails(v => !v);
              }}
              startIcon={
                showDetails ? (
                  <ExpandLessIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{ ...actionSx, color: c.inkSecondary }}
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </Button>
          )}
          {canViewLogs && (
            <Button
              size="small"
              variant="outlined"
              onClick={e => {
                e.stopPropagation();
                onViewLogs?.();
              }}
              startIcon={<LogsIcon sx={{ fontSize: 14 }} />}
              sx={actionSx}
            >
              View failed step logs
            </Button>
          )}
          {view.link && (
            <Button
              size="small"
              variant="outlined"
              component="a"
              href={view.link.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
              sx={actionSx}
            >
              {view.link.label}
            </Button>
          )}
        </Box>
      )}

      {hasDetails && showDetails && (
        <Box
          sx={{
            mt: 0.75,
            p: 1.5,
            borderRadius: `${rounded.sm}px`,
            backgroundColor: alpha(statusHues.failed, 0.08),
            border: `1px solid ${alpha(statusHues.failed, 0.28)}`,
            maxHeight: 280,
            overflow: 'auto',
          }}
        >
          <Typography
            variant="body2"
            component="pre"
            data-testid="error-details"
            sx={{
              m: 0,
              fontFamily: MONO,
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: c.ink,
            }}
          >
            {view.details}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DeploymentError;
